import React, { useState, useEffect, useContext, useRef, useCallback } from "react";

import {
  IconButton,
  Grid,
  Paper,
  Popover,
  Tooltip,
  Dialog,
  CircularProgress,
} from "@material-ui/core";
import { useHistory, useLocation } from "react-router-dom";
import { ArrowBack } from "@material-ui/icons";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import FilterListIcon from "@material-ui/icons/FilterList";
import GetAppIcon from "@material-ui/icons/GetApp";
import DescriptionOutlinedIcon from "@material-ui/icons/DescriptionOutlined";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import MilestoneTrackingAPI from "apis/MilestoneTracking/MilestoneTracking";
import Context from "context/context";
import { toast } from "react-toastify";


const GB_BLUE = "#0a72ba";
const GB_BLUE_SOFT = "#40b5e4";
const GB_BLUE_PALE = "#93d8f0";
const GB_TRACK = "#d7e6f5";
const GB_TITLE = "#152C5B";
const GB_LABEL = "#6b7280";
const GB_VALUE = "#1A1C2E";
const GB_NEGATIVE = "#dc2626";
const GB_GREY = "#9CA3AF"; // grey used for the mini scroll thumb
const GB_FONT = "'DM Sans', sans-serif";

const TX_PAGE_SIZE = 5;

// Width of one milestone cell in the stepper (the line is drawn from the
// center of the first dot to the center of the last dot using this value).
// Narrower so ~10 milestones are visible at once, like the reference design.
const MS_NODE_W = 62;

// How many milestone cells sit to the LEFT of the current one when the
// stepper auto-scrolls (reference design shows 3 achieved before current).
const MS_LEADING_NODES = 3;

// Breathing room at the left/right ends of the scrollable stepper so wide
// labels (e.g. "$5,989 to go") are never clipped on the first/last milestone.
const MS_EDGE_PAD = 24;

// 1 Thrive Rewards point = $0.005 (so a $500 bonus = 100,000 Growth Bonus Points).
const POINT_VALUE = 0.005;

// "View Thrive Rewards program terms" redirect.
const PROGRAM_TERMS_URL = "https://www.henryschein.com/en-us/about/thrive/terms-and-conditions";

// Milestone line animation timings (seconds) — tweak these to taste.
const GB_LINE_FILL_SECS = 10.5; // blue line growing left → right
const GB_SHIMMER_SECS = 3.5; // white shimmer sweeping along the line

// Info tooltip with a wider max width so the longer explanatory copy
// wraps nicely instead of becoming a tall narrow box.
const InfoTooltip = withStyles({
  tooltip: {
    color: "#111827",
    backgroundColor: "#e8eaeb",
    maxWidth: 360,
    fontSize: 12,
    lineHeight: 1.45,
    fontFamily: GB_FONT,
  },
  arrow: {
    "&:before": {
      backgroundColor: "#e8eaeb",
    },
  },
})(Tooltip);

const filterLabelStyle = { display: "block", fontSize: 12, color: GB_LABEL, marginBottom: 4, fontWeight: 600 };
const filterInputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e9f2",
  fontSize: 13,
  fontFamily: GB_FONT,
  boxSizing: "border-box",
};

// Default milestone ladder, used the instant the page mounts (before the
// config API responds) so the page never has to block on it, and again as
// a fallback if that call fails.
function defaultMilestones() {
  return [
    10000, 15000, 20000, 25000, 30000,
    35000, 40000, 45000, 50000, 55000,
    60000, 65000, 70000, 75000, 80000,
    85000, 90000, 95000, 100000, 105000,
    110000,
  ];
}

function hasUsableGrowthData(obj) {
  return !!obj && obj.netQualifyingSpend !== undefined && obj.netQualifyingSpend !== null;
}

function useCountUp(target, active, { duration = 900, decimals = 0 } = {}) {
  const [value, setValue] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    const startTime = performance.now();
    const endValue = Number(target) || 0;
    const factor = Math.pow(10, decimals);

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(endValue * eased * factor) / factor);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, active, duration, decimals]);

  return value;
}

function fmtMoney(n, decimals = 0) {
  return `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}

function fmtAmount(n) {
  const num = Number(n || 0);
  const sign = num < 0 ? "-" : "";
  return `${sign}$${Math.abs(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// Single date format used everywhere on this page (and the home screen):
// non-padded day, e.g. "Jun 2, 2026".
function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Thrive Rewards points earned on a transaction row.
function getTxPoints(row) {
  const v = row?.thriveRewardsPoints ?? row?.points ?? row?.rewardPoints;
  return v === undefined || v === null || v === "" ? null : Number(v);
}

// "Purchase" or "Return/Credit" (uses the API value if provided).
function getTxType(row) {
  if (row?.transactionType) return row.transactionType;
  return Number(row?.purchaseAmount) < 0 ? "Return/Credit" : "Purchase";
}

// Centered loading symbol. `overlay` places it over the nearest
// position:relative parent; otherwise it is centered in normal flow.
function Loader({ size = 36, overlay = false, minHeight }) {
  const spinner = <CircularProgress size={size} thickness={4} style={{ color: GB_BLUE }} />;
  if (overlay) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,255,255,0.55)",
          zIndex: 5,
          borderRadius: 12,
        }}
      >
        {spinner}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: minHeight || 0 }}>
      {spinner}
    </div>
  );
}

const useStyles = makeStyles(() => ({
  page: {
    minHeight: "100vh",
    background: "#F5F6FA",
    padding: 30,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  pageLoading: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#F5F6FA",
  },
  wrapper: {
    width: "100%",
    maxWidth: 1400,
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: 20,
  },
  backBtn: {
    background: "#d0e8f5",
    marginRight: 15,
    color: GB_BLUE,
    "&:hover": { background: "#b8d8ee" },
  },
  title: {
    fontSize: 26,
    fontWeight: 800,
    color: GB_TITLE,
    letterSpacing: 0.2,
    fontFamily: GB_FONT,
  },

  /* Hero */
  hero: {
    background: `linear-gradient(135deg, ${GB_BLUE} 0%, ${GB_BLUE_SOFT} 100%)`,
    borderRadius: 20,
    padding: "22px 28px",
    color: "#fff",
    marginBottom: 20,
    fontFamily: GB_FONT,
  },
  heroPeriod: {
    fontSize: 15,
    fontWeight: 400,
    fontFamily: GB_FONT,
  },
  statsRow: {
    marginTop: 18,
  },
  // Qualifying Spend + Points boxes: content centered horizontally AND vertically.
  statCard: {
    background: "rgba(255,255,255,0.18)",
    borderRadius: 14,
    padding: "16px 18px",
    textAlign: "center",
    border: "1px solid rgba(255,255,255,0.25)",
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  statCardAlt: {
    background: "rgba(255,255,255,0.18)",
    borderRadius: 14,
    padding: "16px 20px",
    textAlign: "left",
    border: "1px solid rgba(255,255,255,0.25)",
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  // "All milestones reached!" state: content centred horizontally and vertically.
  statCardAltCentered: {
    alignItems: "center",
    textAlign: "center",
  },
  statLabel: {
    fontSize: 12,
    opacity: 0.85,
    letterSpacing: 0.2,
  },
  // Slightly larger label used by the Qualifying Spend + Points Earned cards.
  statLabelLg: {
    fontSize: 15,
    opacity: 0.9,
    letterSpacing: 0.2,
  },
  statValue: {
    fontSize: 26,
    fontWeight: 800,
    marginTop: 6,
  },
  statSub: {
    fontSize: 11,
    opacity: 0.85,
    marginTop: 3,
  },

  /* Shared white cards (milestones + transactions) */
  whiteCard: {
    background: "#FFFFFF",
    borderRadius: 18,
    padding: "22px 24px",
    border: "1px solid #d0e8f5",
    fontFamily: GB_FONT,
  },
  cardHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 12,
  },
  // Title + badge on one line (milestones card) — matches the reference.
  titleBadgeRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  cardTitle: {
    fontSize: 19,
    fontWeight: 800,
    color: GB_TITLE,
  },
  cardSub: {
    fontSize: 13,
    color: GB_LABEL,
    marginTop: 4,
    maxWidth: 560,
  },
  // Milestones subtitle: spans the full card width and wraps naturally onto a
  // second line ("…for every / additional $5,000."), so it never touches or
  // overflows the card edge.
  msSubtitle: {
    maxWidth: "none",
    fontSize: 12.5,
    lineHeight: 1.5,
  },
  cardBadge: {
    fontSize: 12,
    fontWeight: 700,
    color: GB_TITLE,
    whiteSpace: "nowrap",
  },

  /* Milestone stepper */
  navArrowBtn: {
    width: 36,
    height: 36,
    background: "#fff",
    border: `1px solid ${GB_BLUE_PALE}`,
    color: GB_BLUE,
    "&:hover": { background: "#e8f4fc" },
  },
  msTrack: {
    flex: 1,
    overflowX: "auto",
    overflowY: "hidden",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  msNode: {
    width: MS_NODE_W,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
  },
  msLabelTop: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    fontFamily: GB_FONT,
    whiteSpace: "nowrap",
  },
  msLabelBottom: {
    fontSize: 12,
    color: GB_BLUE,
    marginTop: 6,
    textAlign: "center",
    lineHeight: 1.3,
    fontFamily: GB_FONT,
    whiteSpace: "nowrap",
  },
  msDotAchieved: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: GB_BLUE,
  },
  msDotLocked: {
    width: 16,
    height: 16,
    borderRadius: "50%",
    background: "#fff",
    border: `2px solid ${GB_BLUE_PALE}`,
  },
  msDotCurrentOuter: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    border: `3px solid ${GB_BLUE_PALE}`,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  msDotCurrentInner: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: GB_BLUE,
  },
  miniArrow: {
    fontSize: 18,
    color: "#9CA3AF",
    cursor: "pointer",
  },
  miniScrollTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    background: "#e5e9f2",
    position: "relative",
    overflow: "hidden",
  },
  // Scroll thumb is grey (was blue).
  miniScrollThumb: {
    position: "absolute",
    top: 0,
    height: 6,
    borderRadius: 3,
    background: GB_GREY,
    transition: "left 0.15s ease, width 0.15s ease",
  },

  /* Transactions */
  txIconBadge: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: "#e8f4fc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  txBtn: {
    display: "inline-flex",
    alignItems: "center",
    background: "#fff",
    border: "1px solid #d0e8f5",
    color: GB_TITLE,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 14px",
    cursor: "pointer",
    fontFamily: GB_FONT,
    "&:disabled": { opacity: 0.6, cursor: "default" },
  },
  // Filter button look when at least one filter is applied.
  txBtnActive: {
    background: "#e8f4fc",
    border: `1px solid ${GB_BLUE}`,
    color: GB_BLUE,
  },
  // Count bubble on the Filter button.
  filterBadge: {
    marginLeft: 8,
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: GB_BLUE,
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  },
  // Active-filter chips row.
  chipRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 14,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "#e8f4fc",
    border: `1px solid ${GB_BLUE_PALE}`,
    color: GB_TITLE,
    borderRadius: 16,
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 6px 4px 12px",
    fontFamily: GB_FONT,
  },
  chipClose: {
    cursor: "pointer",
    fontSize: 16,
    color: GB_BLUE,
    lineHeight: 1,
  },

  /* Table header / cells — matched to the reference image.
     No divider lines between rows; the header is a soft rounded bar. */
  txTable: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  txTh: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 500,
    color: "#4b5563",
    padding: "11px 12px",
    background: "#eef3fb",
    border: "none",
    whiteSpace: "normal",
    verticalAlign: "middle",
    fontFamily: GB_FONT,
    "&:first-child": { borderTopLeftRadius: 10, borderBottomLeftRadius: 10 },
    "&:last-child": { borderTopRightRadius: 10, borderBottomRightRadius: 10 },
  },
  txTd: {
    fontSize: 13,
    fontWeight: 400,
    color: GB_VALUE,
    padding: "9px 12px",
    border: "none",
    whiteSpace: "nowrap",
    fontFamily: GB_FONT,
  },
  txLink: {
    color: GB_BLUE,
    textDecoration: "underline",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
  },
  paginationRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 14,
    flexWrap: "wrap",
    gap: 10,
  },
  pageArrowBtn: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "1px solid #e5e9f2",
    color: GB_TITLE,
  },
  pageBtn: {
    width: 30,
    height: 30,
    borderRadius: 8,
    border: "1px solid #e5e9f2",
    background: "#fff",
    color: GB_TITLE,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: GB_FONT,
  },
  pageBtnActive: {
    background: "#e8f4fc",
    border: `1px solid ${GB_BLUE}`,
    color: GB_BLUE,
  },
  viewAllLink: {
    display: "flex",
    alignItems: "center",
    color: GB_BLUE,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    fontFamily: GB_FONT,
  },

  /* Detail dialog table (View details popup) */
  detailTh: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 700,
    color: GB_TITLE,
    padding: "10px 12px",
    background: "#cfe3f7",
    whiteSpace: "nowrap",
  },
  detailTd: {
    fontSize: 13,
    color: GB_VALUE,
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    whiteSpace: "nowrap",
  },

  /* Sidebar — "How It Works" (stretches to the bottom of the transactions card) */
  sidebarCard: {
    background: "#FFFFFF",
    borderRadius: 18,
    padding: "16px 12px",
    border: "1px solid #d0e8f5",
    fontFamily: GB_FONT,
    flex: 1,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
  },
  sidebarHeading: {
    fontSize: 20,
    fontWeight: 800,
    color: GB_TITLE,
    marginBottom: 10,
  },
  sidebarSection: {
    background: "#f1f6fd",
    border: "1px solid #e1ebf8",
    borderRadius: 12,
    padding: "10px 12px",
    marginBottom: 10,
  },
  sidebarSectionHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 800,
    color: GB_TITLE,
    userSelect: "none",
    lineHeight: 1.3,
  },
  sidebarSectionBody: {
    fontSize: 11.5,
    color: "#4b5563",
    lineHeight: 1.45,
    marginTop: 6,
  },
}));

export default function GrowthBonusDashboard() {
  const classes = useStyles();
  const history = useHistory();
  const {
    shiptomilestoneView,
    getMilestoneConfig,
    getQualifyingTransactions,
    getQualifyingTransactionLines,
    downloadQualifyingTransactions,
  } = MilestoneTrackingAPI();
  const { state, dispatch } = useContext(Context);
  const location = useLocation();

  const initialGrowthData = hasUsableGrowthData(location?.state?.data) ? location.state.data : null;

  const [data, setData] = useState(initialGrowthData);
  const [loading, setLoading] = useState(!initialGrowthData);

  const [milestones, setMilestones] = useState(defaultMilestones());
  const [shipto, setShipto] = useState("");

  const [animationStarted, setAnimationStarted] = useState(false);

  const milestonesCardRef = useRef(null);
  const trackRef = useRef(null);
  const [scrollMetrics, setScrollMetrics] = useState({ scrollLeft: 0, scrollWidth: 1, clientWidth: 1 });

  const [txPage, setTxPage] = useState(1);
  const [txRows, setTxRows] = useState([]);
  const [txTotalCount, setTxTotalCount] = useState(0);
  const [txTotalPages, setTxTotalPages] = useState(1);
  const [txLoading, setTxLoading] = useState(false);
  const [txLoaded, setTxLoaded] = useState(false); // true after the first fetch finishes
  const [txFilters, setTxFilters] = useState({ orderDate: "", orderNumber: "" });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [downloading, setDownloading] = useState(false);

  // "How It Works" sidebar accordion (all open by default)
  const [openSections, setOpenSections] = useState({ earn: true, spend: true, stack: true });
  const toggleSection = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  // View details popup (per-order line items)
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOrderNumber, setDetailOrderNumber] = useState(null);
  const [detailLines, setDetailLines] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (location?.state?.data) {
      const dataObj = location.state.data;

      const accountNumber = String(
        dataObj?.customer_account_number ||
        dataObj?.shipto ||
        ""
      ).trim();

      if (!accountNumber) {
        toast.error("Missing account number — couldn't load Growth Bonus details.");
      }

      setShipto(accountNumber);
    } else {
      history.goBack();
    }
  }, [location, history]);

  useEffect(() => {
    dispatch({
      type: "SET_PATHNAME",
      payload: ["HOME", "ADMIN", "MILESTONE", "VIEW"],
    });
  }, [dispatch]);

  useEffect(() => {
    async function fetchMilestoneConfig() {
      try {
        const res = await getMilestoneConfig();
        if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
          const sorted = [...res.data].sort((a, b) => a.milestone_value - b.milestone_value);
          setMilestones(sorted.map((m) => Number(m.milestone_value)));
        } else {
          setMilestones(defaultMilestones());
        }
      } catch (err) {
        console.error("Failed to load milestone config:", err);
        setMilestones(defaultMilestones());
      }
    }
    fetchMilestoneConfig();
  }, []);

  useEffect(() => {
    if (shipto) {
      getData(Boolean(initialGrowthData));
    }
  }, [shipto]);

  async function getData(silent = false) {
    if (!shipto) {
      setLoading(false);
      return;
    }
    try {
      const values = { customer_account_number: shipto };
      if (!silent) {
        dispatch({ type: "SET_LOADING", payload: true });
        setLoading(true);
      }
      const res = await shiptomilestoneView(values);
      if (res?.data) {
        setData(res.data);
      }
    } catch (e) {
      toast.error("Failed to fetch milestone data");
    } finally {
      setLoading(false);
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }

  // Fetch the Qualifying Transactions page whenever the account, page, or
  // applied filters change (covers filter apply, filter remove, next/prev page).
  useEffect(() => {
    if (!shipto) return;
    let cancelled = false;
    (async () => {
      setTxLoading(true);
      try {
        const res = await getQualifyingTransactions({
          customer_account_number: shipto,
          page: txPage,
          pageSize: TX_PAGE_SIZE,
          ...appliedFilters,
        });
        if (!cancelled && res?.data) {
          setTxRows(res.data.rows || []);
          setTxTotalCount(res.data.totalCount || 0);
          setTxTotalPages(res.data.totalPages || 1);
        }
      } catch (e) {
        if (!cancelled) toast.error("Failed to load qualifying transactions");
      } finally {
        if (!cancelled) {
          setTxLoading(false);
          setTxLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [shipto, txPage, appliedFilters]);

  const applyFilters = () => {
    // Single "Order date" filter maps to both the start and end bound on
    // the backend, giving an exact-day match.
    setTxLoading(true);
    setAppliedFilters({
      startDate: txFilters.orderDate || null,
      endDate: txFilters.orderDate || null,
      orderNumber: txFilters.orderNumber || null,
    });
    setTxPage(1);
  };

  const clearFilters = () => {
    setTxLoading(true);
    setTxFilters({ orderDate: "", orderNumber: "" });
    setAppliedFilters({});
    setTxPage(1);
  };

  // How many filters are currently applied (drives badge, chips, empty state).
  const activeFilterCount =
    (appliedFilters.startDate ? 1 : 0) + (appliedFilters.orderNumber ? 1 : 0);

  // Remove a single filter via its chip's "×".
  const removeFilter = (key) => {
    const next = { ...txFilters, [key]: "" };
    setTxLoading(true);
    setTxFilters(next);
    setAppliedFilters({
      startDate: next.orderDate || null,
      endDate: next.orderDate || null,
      orderNumber: next.orderNumber || null,
    });
    setTxPage(1);
  };

  const goToPage = (p) => {
    if (p === txPage) return;
    setTxLoading(true);
    setTxPage(p);
  };

  // Clicking the order number OR "View details" opens the popup with the
  // per-order line items.
  const openDetails = async (orderNumber) => {
    setDetailOrderNumber(orderNumber);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailLines([]);
    try {
      const res = await getQualifyingTransactionLines({ orderNumber, customer_account_number: shipto });
      setDetailLines(res?.data?.lines || []);
    } catch (e) {
      toast.error("Failed to load transaction details");
      setDetailLines([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const rawBlob = await downloadQualifyingTransactions({ customer_account_number: shipto, ...appliedFilters });

      // The backend prefixes negative numbers with an apostrophe (e.g. '-280.00),
      // which Excel shows literally and treats as text. Strip it so the cell is a
      // real number (-280.00). Only a leading ' directly before a negative number
      // that fills the whole CSV cell is touched.
      const buffer = await rawBlob.arrayBuffer();
      const csvText = new TextDecoder("utf-8", { ignoreBOM: true }).decode(buffer);
      const cleanedCsv = csvText.replace(
        /(^|,|\n)(")?'(-\d[\d.]*)(")?(?=,|\r?\n|$)/g,
        "$1$2$3$4"
      );
      const blob = new Blob([cleanedCsv], { type: "text/csv;charset=utf-8;" });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qualifying-transactions-${shipto}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download transactions");
    } finally {
      setDownloading(false);
    }
  };

  // Trigger the reveal animations once the milestones card is on screen.
  useEffect(() => {
    if (!milestonesCardRef.current) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !animationStarted) {
            setAnimationStarted(true);
          }
        });
      },
      { threshold: 0.3 }
    );
    observer.observe(milestonesCardRef.current);
    return () => observer.disconnect();
  }, [milestonesCardRef, animationStarted, loading]);

  const currentSpend = Number(data?.netQualifyingSpend || 0);
  const earnedDollars = Number(data?.bonusDollar || 0);
  const milestonesHitFromPoints = data?.milestonesHit || 0;
  const TOTAL = milestones.length;

  const gbBonusPerMilestone = 500;
  // $500 bonus = 100,000 Growth Bonus Points (at $0.005 per point)
  const gbBonusPoints = Math.round(gbBonusPerMilestone / POINT_VALUE);
  // From the milestoneView API (bonusPoints = total_points_awarded) — no frontend conversion.
  const earnedPoints = Number(data?.bonusPoints || 0);

  // index of the first milestone not yet reached ("current" / in-progress one)
  const currentIdx = milestones.findIndex((m) => currentSpend < m);
  const gbAllMilestonesReached = milestones.length > 0 && currentIdx === -1;
  const nextMile = currentIdx === -1 ? null : milestones[currentIdx];
  const toNext = nextMile ? nextMile - currentSpend : 0;

  const lineEndIndex = currentIdx === -1 ? milestones.length - 1 : currentIdx;
  const lineProgressPct = milestones.length > 1 ? (lineEndIndex / (milestones.length - 1)) * 100 : 0;

  const firstMilestone = milestones[0] || 10000;
  const stepAmount = milestones.length > 1 ? milestones[1] - milestones[0] : 5000;
  const lastMilestone = milestones[milestones.length - 1] || 110000;
  const maxBonusDollars = TOTAL * gbBonusPerMilestone;
  const maxBonusPoints = TOTAL * gbBonusPoints;

  const animatedSpend = useCountUp(currentSpend, animationStarted, { decimals: 0 });
  const animatedPoints = useCountUp(earnedPoints, animationStarted, { decimals: 0 });
  const animatedBonus = useCountUp(earnedDollars, animationStarted, { decimals: 0 });
  const animatedToNext = useCountUp(toNext, animationStarted, { decimals: 0 });

  const programStart = data?.programStartDate;
  const programEnd = data?.programEndDate;

  const updateScrollMetrics = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setScrollMetrics({ scrollLeft: el.scrollLeft, scrollWidth: el.scrollWidth, clientWidth: el.clientWidth });
  }, []);

  useEffect(() => {
    updateScrollMetrics();
    window.addEventListener("resize", updateScrollMetrics);
    return () => window.removeEventListener("resize", updateScrollMetrics);
  }, [updateScrollMetrics, milestones]);

  const scrollByAmount = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * MS_NODE_W * 4, behavior: "smooth" });
  };

  // Auto-scroll the stepper once the card becomes visible and data has
  // loaded, so the current milestone sits a few cells in from the left
  // (a few achieved milestones to its left, locked ones to its right) —
  // same framing as the reference design.
  useEffect(() => {
    if (!animationStarted || !trackRef.current || milestones.length === 0) return;
    const targetIndex = currentIdx === -1 ? milestones.length - 1 : currentIdx;
    const nodes = trackRef.current.querySelectorAll(".gb-ms-node");
    const node = nodes[targetIndex];
    if (node) {
      const offset = node.offsetLeft + MS_EDGE_PAD - MS_NODE_W * MS_LEADING_NODES;
      trackRef.current.scrollTo({ left: Math.max(0, offset), behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animationStarted, milestones, currentIdx]);

  const thumbWidthPct = scrollMetrics.scrollWidth > 0
    ? Math.min(100, (scrollMetrics.clientWidth / scrollMetrics.scrollWidth) * 100)
    : 100;
  const thumbLeftPct = scrollMetrics.scrollWidth > scrollMetrics.clientWidth
    ? (scrollMetrics.scrollLeft / (scrollMetrics.scrollWidth - scrollMetrics.clientWidth)) * (100 - thumbWidthPct)
    : 0;

  // Visible page-number window (max 5) that always contains the current page.
  const pageWindowStart = Math.max(1, Math.min(txPage - 2, txTotalPages - 4));
  const pageNumbers = Array.from({ length: Math.min(5, txTotalPages) }, (_, i) => pageWindowStart + i);

  // Full-page centered loader while the main milestone data is fetched.
  if (loading && !data) {
    return (
      <div className={classes.pageLoading}>
        <Loader size={48} />
      </div>
    );
  }

  return (
    <div className={classes.page}>
      <style>{`
        @keyframes gbFadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gbNodeIn {
          from { opacity: 0; transform: translateY(10px) scale(0.85); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes gbLineShimmer {
          0% { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }
        @keyframes gbCurrentPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(10,114,186,0.35); }
          50% { box-shadow: 0 0 0 8px rgba(10,114,186,0); }
        }
      `}</style>
      <div className={classes.wrapper}>

        {/* ── HEADER ── */}
        <div className={classes.header}>
          <IconButton className={classes.backBtn} onClick={() => history.goBack()}>
            <ArrowBack />
          </IconButton>
          <div className={classes.title}>Thrive Unlocked: Growth Bonus Points</div>
        </div>

        {/* ── HERO ── */}
        <Paper className={classes.hero} elevation={3} style={{ animation: "gbFadeInUp 0.45s ease-out" }}>
          <div className={classes.heroPeriod}>
            <strong>Your Program Period:</strong>{" "}
            {programStart && programEnd
              ? `${fmtDate(programStart)} – ${fmtDate(programEnd)}`
              : "—"}
          </div>

          <Grid container spacing={2} className={classes.statsRow} alignItems="stretch">
            <Grid item xs={12} sm={4}>
              <div className={classes.statCard}>
                <div className={classes.statLabelLg}>Qualifying Merchandise Spend</div>
                <div className={classes.statValue}>{fmtMoney(animatedSpend, 0)}</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <div className={classes.statCard}>
                <div className={classes.statLabelLg}>Growth Bonus Points Earned</div>
                <div className={classes.statValue}>{animatedPoints.toLocaleString("en-US")} points</div>
                <div className={classes.statSub} style={{ fontSize: 13 }}>({fmtMoney(animatedBonus, 0)})</div>
              </div>
            </Grid>

            <Grid item xs={12} sm={4}>
              <div className={`${classes.statCardAlt} ${gbAllMilestonesReached ? classes.statCardAltCentered : ""}`}>
                {gbAllMilestonesReached ? (
                  <div style={{ fontWeight: 800, fontSize: 18, textAlign: "center" }}>🎉 All milestones reached!</div>
                ) : (
                  <>
                    <div style={{ fontSize: 18, fontWeight: 800 }}>
                      Spend {fmtMoney(animatedToNext, 0)} more
                    </div>
                    <div className={classes.statSub} style={{ marginTop: 4, fontSize: 12 }}>
                      {`to earn your next ${gbBonusPoints.toLocaleString("en-US")} Growth Bonus Points ($${gbBonusPerMilestone})`}
                    </div>
                  </>
                )}
              </div>
            </Grid>
          </Grid>
        </Paper>

        {/* ── MAIN (milestones + transactions) + SIDEBAR ── */}
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={9} style={{ display: "flex", flexDirection: "column" }}>

            {/* MILESTONES CARD */}
            <Paper className={classes.whiteCard} elevation={0} ref={milestonesCardRef}>
              {/* Title + "x of y milestones reached" badge share one line */}
              <div className={classes.titleBadgeRow}>
                <div className={classes.cardTitle}>Your Growth Bonus Milestones</div>
                <div className={classes.cardBadge}>{milestonesHitFromPoints} of {TOTAL} milestones reached</div>
              </div>
              {/* Subtitle: smaller font so it fits on a single line on desktop */}
              <div className={`${classes.cardSub} ${classes.msSubtitle}`}>
                {`Earn ${gbBonusPoints.toLocaleString("en-US")} Growth Bonus Points ($${gbBonusPerMilestone}) when you reach $${firstMilestone.toLocaleString()} in qualifying merchandise spend, then ${gbBonusPoints.toLocaleString("en-US")} more for every additional $${stepAmount.toLocaleString()}.`}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 22 }}>
                <IconButton className={classes.navArrowBtn} onClick={() => scrollByAmount(-1)}>
                  <ChevronLeft />
                </IconButton>

                <div ref={trackRef} onScroll={updateScrollMetrics} className={classes.msTrack}>
                  <div style={{ display: "flex", width: "max-content", padding: `0 ${MS_EDGE_PAD}px` }}>
                  <div style={{ position: "relative", display: "inline-flex" }}>
                    {/* Line runs from the CENTER of the first dot to the CENTER of the
                        last dot, so the blue fill stops exactly on the current milestone. */}
                    <div
                      style={{
                        position: "absolute", left: MS_NODE_W / 2, right: MS_NODE_W / 2, top: 46, height: 2,
                        background: GB_TRACK,
                      }}
                    >
                      <div
                        style={{
                          position: "relative", height: "100%",
                          width: animationStarted ? `${lineProgressPct}%` : "0%",
                          background: GB_BLUE,
                          transition: `width ${GB_LINE_FILL_SECS}s cubic-bezier(0.22, 1, 0.36, 1)`,
                          overflow: "hidden",
                        }}
                      >
                        {animationStarted && lineProgressPct > 0 && (
                          <div
                            style={{
                              position: "absolute", top: 0, left: 0, height: "100%", width: "40%",
                              background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 50%, rgba(255,255,255,0) 100%)",
                              animation: `gbLineShimmer ${GB_SHIMMER_SECS}s ease-in-out infinite`,
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {milestones.map((m, i) => {
                      const achieved = currentSpend >= m;
                      const isCurrent = i === currentIdx;
                      return (
                        <div
                          key={m}
                          className={`${classes.msNode} gb-ms-node`}
                          style={{
                            opacity: animationStarted ? 1 : 0,
                            animation: animationStarted ? `gbNodeIn 0.45s ease-out ${i * 0.05}s both` : "none",
                          }}
                        >
                          <div
                            className={classes.msLabelTop}
                            style={{ color: achieved || isCurrent ? GB_TITLE : "#9CA3AF" }}
                          >
                            {m >= 1000 ? `$${m / 1000}K` : `$${m}`}
                          </div>

                          <div style={{ height: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {isCurrent ? (
                              <div
                                className={classes.msDotCurrentOuter}
                                style={{ animation: "gbCurrentPulse 2.4s ease-in-out infinite" }}
                              >
                                <div className={classes.msDotCurrentInner} />
                              </div>
                            ) : (
                              <div className={achieved ? classes.msDotAchieved : classes.msDotLocked} />
                            )}
                          </div>

                          <div className={classes.msLabelBottom} style={{ fontWeight: isCurrent ? 700 : 600 }}>
                            {isCurrent ? `${fmtMoney(animatedToNext, 0)} to go` : `$${gbBonusPerMilestone}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  </div>
                </div>

                <IconButton className={classes.navArrowBtn} onClick={() => scrollByAmount(1)}>
                  <ChevronRight />
                </IconButton>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16 }}>
                <ChevronLeft className={classes.miniArrow} onClick={() => scrollByAmount(-1)} />
                <div className={classes.miniScrollTrack}>
                  <div
                    className={classes.miniScrollThumb}
                    style={{ width: `${thumbWidthPct}%`, left: `${thumbLeftPct}%` }}
                  />
                </div>
                <ChevronRight className={classes.miniArrow} onClick={() => scrollByAmount(1)} />
              </div>
            </Paper>

            {/* QUALIFYING TRANSACTIONS CARD */}
            <Paper className={classes.whiteCard} elevation={0} style={{ marginTop: 20, flex: 1 }}>
              <div className={classes.cardHeaderRow}>
                <div style={{ display: "flex", gap: 10, flex: "1 1 320px", minWidth: 0 }}>
                  <div className={classes.txIconBadge}>
                    <DescriptionOutlinedIcon style={{ fontSize: 18, color: GB_BLUE }} />
                  </div>
                  <div>
                    <div className={classes.cardTitle}>Qualifying Transactions</div>
                    <div className={classes.cardSub} style={{ maxWidth: "none", fontSize: 12.5 }}>
                      These transactions contribute to your Qualifying Merchandise Spend and may also earn Thrive Rewards points.
                    </div>
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
                  <button
                    type="button"
                    className={`${classes.txBtn} ${activeFilterCount > 0 ? classes.txBtnActive : ""}`}
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                  >
                    <FilterListIcon fontSize="small" style={{ marginRight: 6 }} /> Filter
                    {activeFilterCount > 0 && (
                      <span className={classes.filterBadge}>{activeFilterCount}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={classes.txBtn}
                    onClick={handleDownload}
                    disabled={downloading || txTotalCount === 0}
                  >
                    {downloading ? (
                      <CircularProgress size={16} thickness={5} style={{ color: GB_BLUE, marginRight: 8 }} />
                    ) : (
                      <GetAppIcon fontSize="small" style={{ marginRight: 6 }} />
                    )}
                    {downloading ? "Downloading…" : "Download"}
                  </button>
                </div>

                <Popover
                  open={Boolean(filterAnchorEl)}
                  anchorEl={filterAnchorEl}
                  onClose={() => {
                    setFilterAnchorEl(null);
                    // Discard unapplied edits so the inputs always reflect
                    // what's actually applied.
                    setTxFilters({
                      orderDate: appliedFilters.startDate || "",
                      orderNumber: appliedFilters.orderNumber || "",
                    });
                  }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <div style={{ padding: 20, width: 260, fontFamily: GB_FONT }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: GB_TITLE, marginBottom: 12 }}>
                      Filter transactions
                    </div>

                    <div style={{ marginBottom: 10 }}>
                      <label style={filterLabelStyle}>Order date</label>
                      <input
                        type="date"
                        style={filterInputStyle}
                        value={txFilters.orderDate}
                        onChange={(e) => setTxFilters((f) => ({ ...f, orderDate: e.target.value }))}
                      />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                      <label style={filterLabelStyle}>Order number</label>
                      <input
                        type="text"
                        style={filterInputStyle}
                        placeholder="e.g. 123456"
                        value={txFilters.orderNumber}
                        onChange={(e) => setTxFilters((f) => ({ ...f, orderNumber: e.target.value }))}
                      />
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                      <button
                        type="button"
                        className={classes.txBtn}
                        onClick={() => {
                          clearFilters();
                          setFilterAnchorEl(null);
                        }}
                      >
                        Clear
                      </button>
                      <button
                        type="button"
                        className={classes.txBtn}
                        style={{ background: GB_BLUE, color: "#fff", border: "none" }}
                        onClick={() => {
                          applyFilters();
                          setFilterAnchorEl(null);
                        }}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </Popover>
              </div>

              {/* ── ACTIVE FILTER CHIPS ── */}
              {activeFilterCount > 0 && (
                <div className={classes.chipRow}>
                  <span style={{ fontSize: 12, color: GB_LABEL }}>
                    Filtered: {txTotalCount} result{txTotalCount === 1 ? "" : "s"}
                  </span>
                  {appliedFilters.startDate && (
                    <span className={classes.chip}>
                      Order date: {fmtDate(`${appliedFilters.startDate}T00:00:00`)}
                      <span className={classes.chipClose} onClick={() => removeFilter("orderDate")}>×</span>
                    </span>
                  )}
                  {appliedFilters.orderNumber && (
                    <span className={classes.chip}>
                      Order #: {appliedFilters.orderNumber}
                      <span className={classes.chipClose} onClick={() => removeFilter("orderNumber")}>×</span>
                    </span>
                  )}
                  <span className={classes.txLink} onClick={clearFilters}>Clear all</span>
                </div>
              )}

              {/* ── TABLE AREA (centered loader overlay for first load / filter / pagination) ── */}
              <div style={{ position: "relative", minHeight: txLoading || !txLoaded ? 220 : 0 }}>
                {txLoading && <Loader overlay size={40} />}

                {!txLoaded ? (
                  // First fetch hasn't finished yet — loader only, no empty-state flash.
                  <div style={{ minHeight: 220 }} />
                ) : txRows.length === 0 ? (
                  <div style={{ padding: "32px 0", textAlign: "center", color: "#9CA3AF", fontSize: 13 }}>
                    {txLoading
                      ? ""
                      : activeFilterCount > 0
                        ? "No transactions match your filters."
                        : "No qualifying transactions to display."}
                  </div>
                ) : (
                  <div
                    style={{
                      overflowX: "auto",
                      marginTop: 14,
                      // Keeps the card a steady height on pages with fewer than 5 rows.
                      minHeight: 220,
                      opacity: txLoading ? 0.4 : 1,
                      transition: "opacity 0.15s ease",
                    }}
                  >
                    {/* No divider lines between rows — only the soft header bar. */}
                    <table className={classes.txTable}>
                      <thead>
                        <tr>
                          <th className={classes.txTh}>Order Date</th>
                          <th className={classes.txTh}>Order Number</th>
                          <th className={classes.txTh} style={{ textAlign: "center" }}>Qualifying Merchandise Spend</th>
                          <th className={classes.txTh} style={{ textAlign: "center" }}>
                            {/* No space between the label and the info icon. */}
                            Thrive Rewards Points
                            <InfoTooltip
                              title="Thrive Rewards points earned on each order, including any accelerated or bonus points from your Signature membership. Returns and credits deduct points. Growth Bonus Points are earned separately and shown above."
                              placement="top"
                              arrow
                            >
                              <InfoOutlinedIcon style={{ fontSize: 14, color: GB_BLUE, verticalAlign: "middle", cursor: "pointer", marginLeft: 2 }} />
                            </InfoTooltip>
                          </th>
                          <th className={classes.txTh}>Transaction Type</th>
                          <th className={classes.txTh} style={{ textAlign: "center" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {txRows.map((row, i) => {
                          const pts = getTxPoints(row);
                          const isNegative = Number(row.purchaseAmount) < 0;
                          return (
                            <tr key={row.orderNumber || i}>
                              {/* Order Date — regular, dark */}
                              <td className={classes.txTd}>{fmtDate(row.orderDate)}</td>

                              {/* Order Number — blue underlined link (opens the popup) */}
                              <td className={classes.txTd}>
                                <span className={classes.txLink} onClick={() => openDetails(row.orderNumber)}>
                                  {row.orderNumber}
                                </span>
                              </td>

                              {/* Qualifying Spend — BOLD; red when negative */}
                              <td
                                className={classes.txTd}
                                style={{
                                  textAlign: "center",
                                  color: isNegative ? GB_NEGATIVE : GB_VALUE,
                                  fontWeight: 700,
                                }}
                              >
                                {fmtAmount(row.purchaseAmount)}
                              </td>

                              {/* Points — regular weight; red when negative */}
                              <td
                                className={classes.txTd}
                                style={{
                                  textAlign: "center",
                                  color: pts !== null && pts < 0 ? GB_NEGATIVE : GB_VALUE,
                                  fontWeight: 400,
                                }}
                              >
                                {pts === null ? "—" : pts.toLocaleString("en-US")}
                              </td>

                              {/* Transaction Type — regular */}
                              <td className={classes.txTd}>{getTxType(row)}</td>

                              {/* Action — blue underlined link (opens the popup) */}
                              <td className={classes.txTd} style={{ textAlign: "center" }}>
                                <span className={classes.txLink} onClick={() => openDetails(row.orderNumber)}>
                                  View details
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {txTotalCount > 0 && (
                <div className={classes.paginationRow}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <IconButton
                      size="small"
                      className={classes.pageArrowBtn}
                      disabled={txPage === 1 || txLoading}
                      onClick={() => goToPage(Math.max(1, txPage - 1))}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>

                    {pageNumbers.map((pageNum) => (
                      <button
                        type="button"
                        key={pageNum}
                        className={`${classes.pageBtn} ${txPage === pageNum ? classes.pageBtnActive : ""}`}
                        onClick={() => goToPage(pageNum)}
                        disabled={txLoading}
                      >
                        {pageNum}
                      </button>
                    ))}

                    <IconButton
                      size="small"
                      className={classes.pageArrowBtn}
                      disabled={txPage === txTotalPages || txLoading}
                      onClick={() => goToPage(Math.min(txTotalPages, txPage + 1))}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              )}
            </Paper>
          </Grid>

          {/* ── SIDEBAR: HOW IT WORKS (flex column so the card stretches to the
              same bottom edge as the Qualifying Transactions card) ── */}
          <Grid item xs={12} md={3} style={{ display: "flex", flexDirection: "column" }}>
            <Paper className={classes.sidebarCard} elevation={0}>
              <div className={classes.sidebarHeading}>How It Works</div>

              {/* Earn Bonuses */}
              <div className={classes.sidebarSection}>
                <div className={classes.sidebarSectionHead} onClick={() => toggleSection("earn")}>
                  <span>Earn Bonuses</span>
                  {openSections.earn ? <ExpandLess style={{ color: GB_BLUE }} /> : <ExpandMore style={{ color: GB_BLUE }} />}
                </div>
                {openSections.earn && (
                  <div className={classes.sidebarSectionBody}>
                    <div style={{ marginBottom: 10 }}>
                      {`Earn your first ${gbBonusPoints.toLocaleString("en-US")} Growth Bonus Points ($${gbBonusPerMilestone}) when you reach $${firstMilestone.toLocaleString()} in qualifying merchandise spend, then ${gbBonusPoints.toLocaleString("en-US")} more for every additional $${stepAmount.toLocaleString()}, up to a maximum of ${maxBonusPoints.toLocaleString("en-US")} Growth Bonus Points ($${maxBonusDollars.toLocaleString()}) at $${lastMilestone.toLocaleString()}.`}
                    </div>
                    <div>Points post within 48 hours of reaching each milestone.</div>
                  </div>
                )}
              </div>

              {/* Qualifying Merchandise Spend — link redirects to Henry Schein */}
              <div className={classes.sidebarSection}>
                <div className={classes.sidebarSectionHead} onClick={() => toggleSection("spend")}>
                  <span>Qualifying Merchandise Spend</span>
                  {openSections.spend ? <ExpandLess style={{ color: GB_BLUE }} /> : <ExpandMore style={{ color: GB_BLUE }} />}
                </div>
                {openSections.spend && (
                  <div className={classes.sidebarSectionBody}>
                    <div style={{ marginBottom: 8 }}>
                      Includes eligible merchandise purchases invoiced during your Thrive Unlocked program period.
                      Returns and credits reduce your qualifying spend. Exclusions include OraPharma (Arestin),
                      Colgate products, small equipment, handpieces, pharmaceuticals, refurbished equipment, taxes,
                      shipping, service-related fees, and other non-qualifying items.
                    </div>
                    <a
                      href={PROGRAM_TERMS_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.txLink}
                      style={{ fontSize: 11.5 }}
                    >
                      View Thrive Rewards program terms
                    </a>
                  </div>
                )}
              </div>

              {/* How Your Rewards Stack */}
              <div className={classes.sidebarSection} style={{ marginBottom: 0 }}>
                <div className={classes.sidebarSectionHead} onClick={() => toggleSection("stack")}>
                  <span>How Your Rewards Stack</span>
                  {openSections.stack ? <ExpandLess style={{ color: GB_BLUE }} /> : <ExpandMore style={{ color: GB_BLUE }} />}
                </div>
                {openSections.stack && (
                  <div className={classes.sidebarSectionBody}>
                    You earn Growth Bonus Points on top of your regular Thrive Rewards points, plus any accelerated
                    or bonus points from your Signature membership.
                  </div>
                )}
              </div>
            </Paper>
          </Grid>
        </Grid>

      </div>

      {/* ── VIEW DETAILS DIALOG (per-order line items) ── */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="lg" fullWidth>
        <div style={{ padding: "20px 24px", fontFamily: GB_FONT }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: GB_TITLE }}>
              Order {detailOrderNumber}
            </div>
            <HighlightOffIcon
              style={{ cursor: "pointer", color: GB_NEGATIVE, fontSize: 26 }}
              onClick={() => setDetailOpen(false)}
            />
          </div>

          {detailLoading ? (
            <Loader size={32} minHeight={120} />
          ) : detailLines.length === 0 ? (
            <div style={{ padding: "32px 0", textAlign: "center", color: "#9CA3AF" }}>
              No line items found for this order.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th className={classes.detailTh}>Item Code</th>
                    <th className={classes.detailTh}>Line #</th>
                    <th className={classes.detailTh}>Product Description</th>
                    <th className={classes.detailTh}>Order Date</th>
                    <th className={classes.detailTh}>Post Date</th>
                    <th className={classes.detailTh}>Order Amount</th>
                    <th className={classes.detailTh}>Earn Rate</th>
                    <th className={classes.detailTh}>Promo Description</th>
                    <th className={classes.detailTh}>Promo Multiplier</th>
                    <th className={classes.detailTh}>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {detailLines.map((l, i) => (
                    <tr key={i} style={{ background: i % 2 === 1 ? "#f8fafc" : "#fff" }}>
                      <td className={classes.detailTd} style={{ color: GB_BLUE, fontWeight: 600 }}>{l.itemCode}</td>
                      <td className={classes.detailTd} style={{ color: GB_BLUE }}>{l.lineNumber}</td>
                      <td className={classes.detailTd}>{l.productDescription}</td>
                      <td className={classes.detailTd} style={{ color: GB_BLUE }}>{fmtDate(l.orderDate)}</td>
                      <td className={classes.detailTd} style={{ color: GB_BLUE }}>{fmtDate(l.postDate)}</td>
                      <td className={classes.detailTd}>{fmtAmount(l.orderAmount)}</td>
                      <td className={classes.detailTd} style={{ color: GB_BLUE }}>{l.earnRate ? `${l.earnRate}x` : "-"}</td>
                      <td className={classes.detailTd}>{l.promoDescription || "-"}</td>
                      <td className={classes.detailTd} style={{ color: GB_BLUE }}>{l.promoMultiplier ? `${l.promoMultiplier}x` : "-"}</td>
                      <td className={classes.detailTd} style={{ color: GB_BLUE }}>{l.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}